import { useMemo, useState, useRef } from 'react';
import { Box, Card, CardContent, Typography, Button, IconButton, Chip, Alert, CircularProgress } from '@mui/material';
import {
  UploadFile as UploadIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon
} from '@mui/icons-material';
import { useEngagementEditor } from './EngagementEditorContext';
import { AdviseQuote, AdviseSupplier } from '../../../../types/advise';

export const OfficialQuotesPanel = () => {
  const { engagement, suppliers, canEdit, editEngagement } = useEngagementEditor();
  // TODO: `uploading` is set and cleared in the same sync tick (no async boundary), so the uploading UI never renders.
  const [uploading, setUploading] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const quotes = useMemo(() => engagement.uploaded_quotes || [], [engagement.uploaded_quotes]);

  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement.selected_suppliers?.includes(v.id)),
    [suppliers, engagement.selected_suppliers]
  );

  const getSupplierQuotes = (supplier: AdviseSupplier): AdviseQuote[] => {
    return quotes.filter((q) => q.supplier_name?.toLowerCase() === supplier.supplier_name.toLowerCase());
  };

  const handleUpload = async (supplierName: string, files: FileList | null) => {
    if (!files?.length) return;
    setUploading(supplierName);

    const newQuotes: AdviseQuote[] = Array.from(files).map((file) => ({
      file_url: URL.createObjectURL(file),
      file_name: file.name,
      supplier_name: supplierName,
      extracted_pricing: {
        supplier: supplierName,
        summary: '',
        line_items: []
      }
    }));

    editEngagement({ uploaded_quotes: [...quotes, ...newQuotes] });
    setUploading(null);
  };

  const handleDelete = (index: number) => {
    const removed = quotes[index];
    if (removed?.file_url?.startsWith('blob:')) {
      URL.revokeObjectURL(removed.file_url);
    }
    const updated = quotes.filter((_, i) => i !== index);
    editEngagement({ uploaded_quotes: updated });
  };

  if (selectedSuppliers.length === 0) {
    return <Alert severity="info">No suppliers selected. Add suppliers to upload quotes.</Alert>;
  }

  return (
    <Box className="flex flex-col gap-4">
      <Typography
        variant="body2"
        color="textSecondary">
        Upload official supplier quotes for price comparison. Extracted line items will appear in the Pricing tab.
      </Typography>

      <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {selectedSuppliers.map((supplier) => {
          const supplierQuotes = getSupplierQuotes(supplier);
          const isUploading = uploading === supplier.supplier_name;

          return (
            <Card
              key={supplier.id}
              variant="outlined">
              <CardContent>
                <Box className="flex items-center justify-between mb-3">
                  <Typography variant="subtitle2">{supplier.supplier_name}</Typography>
                  <Chip
                    label={`${supplierQuotes.length} quote${supplierQuotes.length !== 1 ? 's' : ''}`}
                    size="small"
                    color={supplierQuotes.length > 0 ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </Box>

                {supplierQuotes.length > 0 && (
                  <Box className="flex flex-col gap-2 mb-3">
                    {/* TODO: key on quote identity, not array index — deleting mid-list reconciles onto the wrong row. */}
                    {supplierQuotes.map((quote, idx) => {
                      const globalIdx = quotes.indexOf(quote);
                      return (
                        <Box
                          key={idx}
                          className={'flex items-center gap-2 p-2 ' + 'rounded bg-gray-50'}>
                          <FileIcon
                            fontSize="small"
                            color="action"
                          />
                          <Typography
                            variant="body2"
                            className="flex-1 truncate">
                            {quote.file_name}
                          </Typography>
                          {quote.file_url && (
                            <IconButton
                              size="small"
                              href={quote.file_url}
                              target="_blank"
                              rel="noopener noreferrer">
                              <OpenIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canEdit && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(globalIdx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {canEdit && (
                  <>
                    <input
                      type="file"
                      accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
                      multiple
                      hidden
                      ref={(el) => {
                        fileInputRefs.current[supplier.id] = el;
                      }}
                      onChange={(e) => handleUpload(supplier.supplier_name, e.target.files)}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={isUploading ? <CircularProgress size={16} /> : <UploadIcon />}
                      disabled={isUploading}
                      onClick={() => fileInputRefs.current[supplier.id]?.click()}>
                      Upload Quote
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};
